import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";

const FFT = FIELDS.formTemplates;
const FFR = FIELDS.formResponses;

function mapFormTemplate(r: Record<string, unknown>): FormTemplate {
  return { id: r.record_id as number, title: (r[FFT.title] as string) || "", description: (r[FFT.description] as string) || "", schemaJson: (r[FFT.schemaJson] as string) || "", status: (r[FFT.status] as string) || "draft", responseCount: Number(r[FFT.responseCount]) || 0 };
}
function mapFormResponse(r: Record<string, unknown>): FormResponse {
  return { id: r.record_id as number, templateId: (r[FFR.templateId] as number) || 0, respondentName: (r[FFR.respondentName] as string) || "", respondentEmail: (r[FFR.respondentEmail] as string) || "", answersJson: (r[FFR.answersJson] as string) || "", status: (r[FFR.status] as string) || "new", submittedAt: (r[FFR.submittedAt] as string) || "" };
}

interface FormTemplate { id: number; title: string; description: string; schemaJson: string; status: string; responseCount: number; [k: string]: unknown; }
interface FormResponse { id: number; templateId: number; respondentName: string; respondentEmail: string; answersJson: string; status: string; submittedAt: string; [k: string]: unknown; }

interface FormsState {
  templates: FormTemplate[]; responses: FormResponse[]; loading: boolean;
  fetchTemplates: () => Promise<void>; fetchResponses: (templateId?: number) => Promise<void>;
  createTemplate: (data: Record<string, unknown>) => Promise<number | null>;
  updateTemplate: (id: number, data: Record<string, unknown>) => Promise<void>;
  submitResponse: (data: Record<string, unknown>) => Promise<number | null>;
  updateResponseStatus: (id: number, status: string) => Promise<void>;
}

export const useFormsStore = create<FormsState>((set, get) => ({
  templates: [], responses: [], loading: false,
  fetchTemplates: async () => { try { const r = await fetchRecords<FormTemplate>(TABLES.formTemplates, { limit: 200 }); set({ templates: r.records.map(mapFormTemplate) }); } catch (e) { console.error('[Forms]', e); } },
  fetchResponses: async (templateId) => {
    try {
      const opts = templateId ? { filters: [{ field: 'templateId', operator: 'eq' as const, value: templateId }] } : {};
      const r = await queryRecords<FormResponse>(TABLES.formResponses, { limit: 500, sort: { field: 'created_at', direction: 'desc' }, ...opts });
      set({ responses: r.records.map(mapFormResponse) });
    } catch (e) { console.error('[Forms]', e); }
  },
  createTemplate: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.title) p[FFT.title] = data.title; if (data.description) p[FFT.description] = data.description;
    if (data.schemaJson) p[FFT.schemaJson] = data.schemaJson; if (data.status) p[FFT.status] = data.status || 'draft';
    try { const r = await createRecord<FormTemplate>(TABLES.formTemplates, p); await get().fetchTemplates(); return r.id; } catch (e) { console.error('[Forms]', e); return null; }
  },
  updateTemplate: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.title !== undefined) p[FFT.title] = data.title;
    if (data.description !== undefined) p[FFT.description] = data.description;
    if (data.schemaJson !== undefined) p[FFT.schemaJson] = data.schemaJson;
    if (data.status !== undefined) p[FFT.status] = data.status;
    if (data.settingsJson !== undefined) p[FFT.settingsJson] = data.settingsJson;
    try { await updateRecord(TABLES.formTemplates, id, p); await get().fetchTemplates(); } catch (e) { console.error('[Forms]', e); }
  },
  submitResponse: async (data) => {
    const p: Record<string,unknown> = { [FFR.submittedAt]: new Date().toISOString(), [FFR.status]: 'new' };
    if (data.templateId) p[FFR.templateId] = data.templateId; if (data.respondentName) p[FFR.respondentName] = data.respondentName;
    if (data.respondentEmail) p[FFR.respondentEmail] = data.respondentEmail; if (data.answersJson) p[FFR.answersJson] = data.answersJson;
    try { const r = await createRecord<FormResponse>(TABLES.formResponses, p); return r.id; } catch (e) { console.error('[Forms]', e); return null; }
  },
  updateResponseStatus: async (id, status) => { try { await updateRecord(TABLES.formResponses, id, { [FFR.status]: status }); await get().fetchResponses(); } catch (e) { console.error('[Forms]', e); } },

  // V1 compat aliases
  isLoading: false,
  loadTemplates: async () => { await (get() as any).fetchTemplates(); },
  loadResponses: async (templateId?: any) => { await (get() as any).fetchResponses(templateId); },
  loadFormTemplates: async () => { await (get() as any).fetchTemplates(); },
  loadTemplate: async (id: any) => { return (get() as any).templates?.find((t: any) => t.id === id) || null; },
  loadAll: async () => { await (get() as any).fetchTemplates(); await (get() as any).fetchResponses(); },
  updateResponse: async (id: any, data: any) => { await (get() as any).updateResponseStatus(id, data.status ?? data); },
  deleteTemplate: async (id: any) => {},
}));
